import { policySchema, type Policy } from "@contracts";

const MOEL_2027 = "https://moel.go.kr/news/enews/report/enewsView.do?news_seq=19744";
const MOEL_2026 = "https://www.moel.go.kr/mainpop2.do";
const MOHW_PENSION = "https://www.mohw.go.kr/menu.es?mid=a10714060000";
const NPS_PENSION_RATE = "https://ma.nps.or.kr/pnsinfo/ntpsklg/getOHAF0016M0.do?menuId=MN24001108";
const MOHW_HEALTH = "https://www.mohw.go.kr/gallery.es?act=view&bid=0003&list_no=379625&mid=a10607030000";
const MOHW_CARE = "https://www.mohw.go.kr/menu.es?mid=a10710040400";
const LAW_OPEN = "https://open.law.go.kr/LSO/openApi/guideList.do";
const ASSEMBLY_OPEN = "https://www.data.go.kr/data/15125946/openapi.do";
const LAWMAKING = "https://opinion.lawmaking.go.kr/";
const GOV24 = "https://plus.gov.kr/portal/benefitV2/";

const source = (id: string, title: string, publisher: string, url: string, kind: "official_api" | "official_page" | "official_rss" | "official_handoff") => ({
  id,
  title,
  publisher,
  url,
  kind,
  checkedAt: "2026-08-24",
  rightsNote: "공식 사실과 링크만 사용하며 원문 본문을 재배포하지 않는다."
});

const shared = {
  jurisdiction: "대한민국",
  reviewedAt: "2026-08-24",
  staleAfterHours: 168,
  reviewState: "verified" as const,
  visible: true
};

const rows = [
  {
    ...shared,
    id: "POL-WORK-001",
    slug: "minimum-wage-2027",
    family: "work_income",
    title: "2027년 적용 최저임금 10,700원",
    shortTitle: "2027 최저임금",
    summary: "2027년 1월 1일부터 시간급 최저임금이 10,700원으로 바뀝니다. 2026년 10,320원과 비교합니다.",
    stage: "notified",
    confidence: "official_confirmed",
    effectiveAt: "2027-01-01",
    publishedAt: "2026-08-05",
    impactAxes: ["money", "right", "timing"],
    audience: ["임금을 받는 근로자", "근로자를 고용한 사업주"],
    calculatorId: "minimum_wage_2027",
    actionLabel: "고용노동부 발표 확인",
    actionUrl: MOEL_2027,
    sources: [source("SRC-MOEL-2027", "2027년 적용 최저임금 시간급 10,700원", "고용노동부", MOEL_2027, "official_page")],
    issueBalance: {
      established: ["고용노동부가 2027년 적용 시간급과 월 환산액을 공식 발표했습니다."],
      considerations: ["실제 월 임금은 유급 처리 시간과 근로계약 조건에 따라 달라질 수 있습니다."]
    }
  },
  {
    ...shared,
    id: "POL-WORK-002",
    slug: "minimum-wage-2026",
    family: "work_income",
    title: "2026년 적용 최저임금 10,320원",
    shortTitle: "2026 최저임금",
    summary: "2026년 1월 1일부터 12월 31일까지 적용되는 시간급 최저임금 기준입니다.",
    stage: "effective",
    confidence: "official_confirmed",
    effectiveAt: "2026-01-01",
    publishedAt: "2025-08-05",
    impactAxes: ["money", "right"],
    audience: ["임금을 받는 근로자", "근로자를 고용한 사업주"],
    calculatorId: "qualitative",
    actionLabel: "2026년 최저임금 확인",
    actionUrl: MOEL_2026,
    sources: [source("SRC-MOEL-2026", "2026년 적용 최저임금 안내", "고용노동부", MOEL_2026, "official_page")],
    issueBalance: {established: ["2026년 시간급 최저임금은 10,320원입니다."], considerations: ["주휴 등 월 환산 조건은 개인 근로계약을 함께 확인해야 합니다."]}
  },
  {
    ...shared,
    id: "POL-PENSION-001",
    slug: "national-pension-rate-2026",
    family: "pension_care",
    title: "2026년 국민연금 보험료율 9.5%",
    shortTitle: "2026 국민연금",
    summary: "국민연금 보험료율이 2026년 9.5%로 조정됐습니다. 사업장가입자는 사용자와 근로자가 절반씩 부담합니다.",
    stage: "effective",
    confidence: "official_confirmed",
    effectiveAt: "2026-01-01",
    publishedAt: "2025-03-20",
    impactAxes: ["money", "duty"],
    audience: ["국민연금 가입자", "사업장가입 근로자", "지역가입자"],
    calculatorId: "pension_2026",
    actionLabel: "연금개혁 안내 확인",
    actionUrl: NPS_PENSION_RATE,
    sources: [source("SRC-NPS-PENSION-RATE", "연도별 국민연금 보험료율", "국민연금공단", NPS_PENSION_RATE, "official_page")],
    issueBalance: {established: ["2026년 보험료율은 9.5%이며 사업장가입자는 사용자와 근로자가 절반씩 부담합니다."], considerations: ["실제 고지액은 기준소득월액 상·하한과 공단 산정 기준을 따릅니다."]}
  },
  {
    ...shared,
    id: "POL-PENSION-002",
    slug: "national-pension-rate-2027",
    family: "pension_care",
    title: "2027년 국민연금 보험료율 10.0% 예정",
    shortTitle: "2027 국민연금",
    summary: "연금개혁 일정에 따라 보험료율이 2027년 10.0%로 오를 예정입니다. 2026년과의 요율 차이를 계산합니다.",
    stage: "promulgated",
    confidence: "official_confirmed",
    effectiveAt: "2027-01-01",
    publishedAt: "2025-03-20",
    impactAxes: ["money", "duty", "timing"],
    audience: ["국민연금 가입자", "사업장가입 근로자", "지역가입자"],
    calculatorId: "pension_2027",
    actionLabel: "연도별 보험료율 확인",
    actionUrl: NPS_PENSION_RATE,
    sources: [source("SRC-NPS-PENSION-RATE", "연도별 국민연금 보험료율", "국민연금공단", NPS_PENSION_RATE, "official_page")],
    issueBalance: {established: ["보험료율은 2026년부터 매년 0.5%p씩 인상돼 2033년 13%에 도달합니다."], considerations: ["연도별 기준소득월액 상·하한이 별도로 바뀌면 실제 고지액 차이는 달라집니다."]}
  },
  {
    ...shared,
    id: "POL-PENSION-003",
    slug: "birth-credit-first-child",
    family: "pension_care",
    title: "국민연금 출산 크레딧 첫째아 12개월",
    shortTitle: "출산 크레딧",
    summary: "출산 크레딧 대상과 인정 기간 확대 중 첫째아 12개월 인정 여부를 확인하는 정책 항목입니다.",
    stage: "effective",
    confidence: "official_confirmed",
    effectiveAt: "2026-01-01",
    publishedAt: "2025-03-20",
    impactAxes: ["right", "eligibility"],
    audience: ["자녀가 있는 국민연금 가입자"],
    calculatorId: "qualitative",
    actionLabel: "출산 크레딧 안내 확인",
    actionUrl: MOHW_PENSION,
    sources: [source("SRC-MOHW-PENSION", "2025년 국민연금 개혁 주요 내용", "보건복지부", MOHW_PENSION, "official_page")],
    issueBalance: {established: ["공식 개혁 안내에 출산 크레딧 확대가 포함돼 있습니다."], considerations: ["개인 인정 여부와 신청 시점은 국민연금공단 확인이 필요합니다."]}
  },
  {
    ...shared,
    id: "POL-PENSION-004",
    slug: "military-service-credit-12-months",
    family: "pension_care",
    title: "국민연금 군복무 크레딧 12개월",
    shortTitle: "군복무 크레딧",
    summary: "군복무 크레딧 인정 기간이 12개월로 확대되는 변화의 개인 관련성을 확인합니다.",
    stage: "effective",
    confidence: "official_confirmed",
    effectiveAt: "2026-01-01",
    publishedAt: "2025-03-20",
    impactAxes: ["right", "eligibility"],
    audience: ["군복무를 마친 국민연금 가입자"],
    calculatorId: "qualitative",
    actionLabel: "군복무 크레딧 안내 확인",
    actionUrl: MOHW_PENSION,
    sources: [source("SRC-MOHW-PENSION", "2025년 국민연금 개혁 주요 내용", "보건복지부", MOHW_PENSION, "official_page")],
    issueBalance: {established: ["공식 개혁 안내에 군복무 크레딧 인정 기간 확대가 포함돼 있습니다."], considerations: ["개인 가입 이력과 인정 시점은 국민연금공단 확인이 필요합니다."]}
  },
  {
    ...shared,
    id: "POL-HEALTH-001",
    slug: "health-insurance-rate-2026",
    family: "health_care",
    title: "2026년 건강보험료율 7.19%",
    shortTitle: "2026 건강보험",
    summary: "2026년도 건강보험료율 7.19% 확정 사실과 개인 고지액을 계산하기 전 확인할 조건을 보여 줍니다.",
    stage: "effective",
    confidence: "official_confirmed",
    effectiveAt: "2026-01-01",
    publishedAt: "2025-08-28",
    impactAxes: ["money", "duty"],
    audience: ["건강보험 가입자"],
    calculatorId: "qualitative",
    actionLabel: "건정심 결정 확인",
    actionUrl: MOHW_HEALTH,
    sources: [source("SRC-MOHW-HEALTH", "2026년도 건강보험료율 결정", "보건복지부", MOHW_HEALTH, "official_page")],
    issueBalance: {established: ["2026년 건강보험료율은 7.19%로 결정됐습니다."], considerations: ["직장·지역 가입 구분과 보수월액·재산 등 실제 산정 기준을 확인해야 합니다."]}
  },
  {
    ...shared,
    id: "POL-CARE-001",
    slug: "disabled-child-family-support-2026",
    family: "pension_care",
    title: "2026년 장애아가족 양육지원 기준",
    shortTitle: "장애아 돌봄 기준",
    summary: "장애아가족 양육지원의 소득 기준과 공식 신청 안내를 확인할 수 있는 검수 정책 항목입니다.",
    stage: "effective",
    confidence: "official_confirmed",
    effectiveAt: "2026-01-01",
    publishedAt: "2026-01-01",
    impactAxes: ["eligibility", "action"],
    audience: ["장애아동을 돌보는 가족"],
    calculatorId: "qualitative",
    actionLabel: "지원 기준 확인",
    actionUrl: MOHW_CARE,
    sources: [source("SRC-MOHW-CARE", "2026년 장애아가족 양육지원", "보건복지부", MOHW_CARE, "official_page")],
    issueBalance: {established: ["보건복지부가 2026년 지원 대상과 기준을 공개했습니다."], considerations: ["가구 소득과 돌봄 상황별 실제 자격은 신청기관 확인이 필요합니다."]}
  },
  {
    ...shared,
    id: "POL-LAW-001",
    slug: "law-version-history-source",
    family: "civic_law",
    title: "국가법령 개정이력·신구법 공식 확인",
    shortTitle: "법령 변경 원문",
    summary: "법령 본문과 연혁, 조문 변경, 신구법 비교를 공식 공동활용 API에서 확인하는 출처 항목입니다.",
    stage: "source_capability",
    confidence: "official_confirmed",
    effectiveAt: null,
    publishedAt: "2026-08-23",
    impactAxes: ["right", "timing"],
    audience: ["법령 변경을 확인하는 모든 사람"],
    calculatorId: "none",
    actionLabel: "국가법령 API 안내",
    actionUrl: LAW_OPEN,
    sources: [source("SRC-LAW-OPEN", "국가법령정보 공동활용 API", "법제처", LAW_OPEN, "official_api")],
    issueBalance: {established: ["법령 본문·연혁·신구법·조문 변경을 구조화된 공식 데이터로 조회할 수 있습니다."], considerations: ["API 이용 승인과 데이터별 이용조건은 실제 연결 전에 확인해야 합니다."]},
    reviewState: "hidden_without_current_event",
    visible: false
  },
  {
    ...shared,
    id: "POL-BILL-001",
    slug: "assembly-bill-stage-source",
    family: "civic_law",
    title: "국회 의안 단계 공식 추적",
    shortTitle: "국회 의안 단계",
    summary: "열린국회정보 API에서 의안·심사·표결 정보를 받아 정책 생애주기에 연결하는 출처 항목입니다.",
    stage: "source_capability",
    confidence: "official_confirmed",
    effectiveAt: null,
    publishedAt: "2026-08-23",
    impactAxes: ["right", "action", "timing"],
    audience: ["국회 법안을 추적하는 모든 사람"],
    calculatorId: "none",
    actionLabel: "열린국회정보 API 확인",
    actionUrl: ASSEMBLY_OPEN,
    sources: [source("SRC-ASSEMBLY", "국회의원 발의법률안 API", "국회사무처", ASSEMBLY_OPEN, "official_api")],
    issueBalance: {established: ["공공데이터포털의 국회의원 발의법률안 항목은 열린국회정보 operation으로 연결됩니다."], considerations: ["실제 인증키는 열린국회정보 계정에서 별도로 확인해야 합니다."]},
    reviewState: "hidden_without_current_event",
    visible: false
  },
  {
    ...shared,
    id: "POL-ACTION-001",
    slug: "legislative-comment-window-source",
    family: "civic_law",
    title: "정부 입법예고 의견 마감 공식 확인",
    shortTitle: "입법예고 의견",
    summary: "국민참여입법센터의 현재 입법예고와 의견 제출 마감을 정책 행동 큐로 연결하는 출처 항목입니다.",
    stage: "source_capability",
    confidence: "official_confirmed",
    effectiveAt: null,
    publishedAt: "2026-08-23",
    impactAxes: ["action", "timing"],
    audience: ["입법예고에 의견을 내는 모든 사람"],
    calculatorId: "none",
    actionLabel: "현재 입법예고 확인",
    actionUrl: LAWMAKING,
    sources: [source("SRC-LAWMAKING", "국민참여입법센터", "법제처", LAWMAKING, "official_page")],
    issueBalance: {established: ["국민참여입법센터에서 정부 입법예고와 의견 제출 창구를 운영합니다."], considerations: ["현재 event의 원문·마감이 검수되지 않으면 공개 feed에 노출하지 않습니다."]},
    reviewState: "hidden_without_current_event",
    visible: false
  },
  {
    ...shared,
    id: "POL-BENEFIT-001",
    slug: "gov24-benefit-handoff",
    family: "family_benefit",
    title: "정부24 혜택알리미 공식 확인",
    shortTitle: "혜택알리미",
    summary: "현재 받을 수 있는 혜택의 최종 자격과 신청은 정부24 공식 서비스에서 확인하도록 연결합니다.",
    stage: "source_capability",
    confidence: "reviewed_provisional",
    effectiveAt: null,
    publishedAt: "2026-08-23",
    impactAxes: ["eligibility", "action"],
    audience: ["정부 혜택을 확인하는 모든 사람"],
    calculatorId: "none",
    actionLabel: "정부24에서 확인",
    actionUrl: GOV24,
    sources: [source("SRC-GOV24", "정부24 혜택알리미", "행정안전부", GOV24, "official_handoff")],
    issueBalance: {established: ["정부24가 개인별 혜택 확인과 공식 신청 경로를 제공합니다."], considerations: ["바뀜표는 자격을 확정하지 않고 최종 확인을 공식 서비스에 맡깁니다."]},
    reviewState: "review_required",
    visible: false
  }
] satisfies Array<unknown>;

export const policies: Policy[] = rows.map((row) => policySchema.parse(row));
export const visiblePolicies = policies.filter((policy) => policy.visible && policy.reviewState === "verified");
// Public lookups are deliberately built from the verified surface. Hidden source
// capabilities and review drafts must never become addressable through a guessed URL.
export const policyBySlug = new Map(visiblePolicies.map((policy) => [policy.slug, policy]));
export const policyById = new Map(visiblePolicies.map((policy) => [policy.id, policy]));
